'use client';

import { Box, Divider, Stack, Typography } from '@mui/material';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

import { EONLogo } from '@/components/layout/shared/eonLogo';

const LogoTaglineContext = createContext<(tagline: string | undefined) => void>(
  () => undefined
);

export const useLogoTagline = (tagline: string) => {
  const setTagline = useContext(LogoTaglineContext);

  useEffect(() => {
    setTagline(tagline);
    return () => setTagline(undefined);
  }, [setTagline, tagline]);
};

export const BlankLayout = ({ children }: PropsWithChildren) => {
  const [tagline, setTagline] = useState<string | undefined>();

  return (
    <Box
      className='h-full flex flex-col relative'
      style={{ background: 'var(--mui-palette-background-pageGradient)' }}
    >
      <LogoTaglineContext.Provider value={setTagline}>
        {children}
      </LogoTaglineContext.Provider>
      <Box className='absolute top-0 left-0 flex content-center p-[var(--mui-layout-contentInset)]'>
        <Stack direction='row' sx={{ alignItems: 'center', gap: '12px' }}>
          <EONLogo dark />
          {tagline && (
            <>
              <Box
                sx={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--mui-palette-text-primary)',
                }}
              />
              <Typography variant='specialTitle'>{tagline}</Typography>
            </>
          )}
        </Stack>
      </Box>
      <Stack
        className='absolute bottom-0 left-0 p-[var(--mui-layout-wideSpacing)] gap-[24px]'
        direction='row'
        divider={<Divider orientation='vertical' flexItem variant='middle' />}
      >
        <Typography>{`© ${new Date().getFullYear()} Eon. All rights reserved.`}</Typography>
        <a
          href='https://www.eon.io/about'
          target='_blank'
          rel='noreferrer'
          className='flex content-center'
        >
          About Eon
          <i className='material-symbols-open-in-new w-[20px] h-[20px] ml-[8px]'></i>
        </a>
      </Stack>
    </Box>
  );
};
